import React from "react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatRupiah } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { ShoppingBag, Users, Package, Wallet, Activity, ArrowUpRight, ChevronUp, ChevronDown } from "lucide-react";

const PER_PAGE = 10;

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [health, setHealth] = useState(null);
  const [page, setPage] = useState(0);

  useEffect(() => {
    const f = () => {
      api.get("/admin/stats")
        .then((r) => setStats(r.data))
        .catch(() => {});
      api.get("/admin/master-panel/health")
        .then((r) => setHealth(r.data))
        .catch(() => {});
    };
    f();
    const t = setInterval(f, 8000);
    return () => clearInterval(t);
  }, []);

  if (!stats) return <div className="font-mono text-xs">// LOADING DATA</div>;

  const cards = [
    { label: "Revenue", value: formatRupiah(stats.revenue), icon: ArrowUpRight, color: "text-[#00C853]", tid: "stat-revenue" },
    { label: "Total Order", value: stats.total_orders, icon: ShoppingBag, color: "text-[#FF6A00]", tid: "stat-orders" },
    { label: "Pending Order", value: stats.pending_orders, icon: Activity, color: "text-[#FF3333]", tid: "stat-pending" },
    { label: "Total User", value: stats.total_users, icon: Users, color: "text-black", tid: "stat-users" },
    { label: "Produk", value: stats.total_products, icon: Package, color: "text-black", tid: "stat-products" },
    { label: "Top Up Pending", value: stats.pending_deposits, icon: Wallet, color: "text-yellow-500", tid: "stat-deposits" },
  ];

  const allOrders = stats.recent_orders || [];
  const totalPages = Math.ceil(allOrders.length / PER_PAGE);
  const pageOrders = allOrders.slice(page * PER_PAGE, (page + 1) * PER_PAGE);

  return (
    <div data-testid="admin-dashboard">
      <div className="label-mono text-zinc-500">// CONTROL ROOM</div>
      <h1 className="font-display text-3xl md:text-4xl font-black uppercase tracking-tight mt-1">Dashboard</h1>

      <div className="mt-6 grid grid-cols-2 lg:grid-cols-3 gap-4">
        {cards.map((c) => (
          <div key={c.label} className="bg-white border border-black p-5 shadow-brutal" data-testid={c.tid}>
            <div className="flex items-center justify-between">
              <div className="label-mono text-zinc-500">// {c.label}</div>
              <c.icon className={`w-4 h-4 ${c.color}`} />
            </div>
            <div className="font-display text-3xl font-black mt-2">{c.value}</div>
          </div>
        ))}
      </div>

      <div className="mt-6 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 border border-black bg-white">
          <div className="p-4 border-b border-black flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="label-mono">// RECENT ORDERS</div>
              <span className="font-mono text-[10px] text-zinc-500">{page * PER_PAGE + 1}–{Math.min((page + 1) * PER_PAGE, allOrders.length)} dari {allOrders.length}</span>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
                className="p-1.5 border border-black hover:bg-black hover:text-white disabled:opacity-30 disabled:cursor-not-allowed">
                <ChevronUp className="w-3 h-3" />
              </button>
              <span className="font-mono text-[10px]">{page + 1}/{totalPages || 1}</span>
              <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}
                className="p-1.5 border border-black hover:bg-black hover:text-white disabled:opacity-30 disabled:cursor-not-allowed">
                <ChevronDown className="w-3 h-3" />
              </button>
              <Link to="/admin/orders" className="label-mono text-[#FF6A00] ml-2">SEMUA →</Link>
            </div>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 border-b border-black">
              <tr>
                <th className="p-3 text-left label-mono">ORDER</th>
                <th className="p-3 text-left label-mono hidden md:table-cell">USER</th>
                <th className="p-3 text-left label-mono">STATUS</th>
                <th className="p-3 text-right label-mono">TOTAL</th>
              </tr>
            </thead>
            <tbody>
              {pageOrders.map((o) => (
                <tr key={o.id} className="border-b border-zinc-200">
                  <td className="p-3 font-mono text-xs">{o.order_no}</td>
                  <td className="p-3 font-mono text-xs hidden md:table-cell">{o.user_name}</td>
                  <td className="p-3"><StatusBadge status={o.status} /></td>
                  <td className="p-3 text-right font-mono text-xs font-bold">{formatRupiah(o.total)}</td>
                </tr>
              ))}
              {pageOrders.length === 0 && (
                <tr><td colSpan={4} className="p-6 text-center font-mono text-xs text-zinc-500">// no orders yet</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="border border-black bg-black text-white p-5">
          <div className="label-mono text-yellow-400">// MASTER PANEL</div>
          <div className="font-display text-2xl font-black uppercase mt-2">Status</div>
          {health ? (
            <dl className="mt-4 space-y-2 font-mono text-xs">
              <div className="flex justify-between"><dt className="text-zinc-400">STATUS</dt><dd className="text-green-400">{health.status.toUpperCase()}</dd></div>
              <div className="flex justify-between"><dt className="text-zinc-400">VERSION</dt><dd>{health.version}</dd></div>
              <div className="flex justify-between"><dt className="text-zinc-400">UPTIME</dt><dd>{health.uptime}</dd></div>
              <div className="flex justify-between"><dt className="text-zinc-400">LATENCY</dt><dd>{health.latency_ms}ms</dd></div>
              <div className="flex justify-between"><dt className="text-zinc-400">QUEUE</dt><dd>{health.queue_depth}</dd></div>
            </dl>
          ) : <div className="font-mono text-xs mt-4 text-zinc-500">// loading...</div>}
          <div className="mt-4 pt-4 border-t border-zinc-700 font-mono text-[10px] text-zinc-500">
            // MOCKED endpoint - swap with real master panel API
          </div>
        </div>
      </div>
    </div>
  );
}